/* eslint-disable max-lines-per-function */
import classNames from 'classnames'
import { useState } from 'react'
import ArrowDown from '@/assets/common/arrowDown.inline.svg'
import Link from '@/components/Common/Link'
import { StencilCDNImage } from '@/components/Common/StencilImage'
import { StockPeer } from '@/components/Stocks/Widgets/types'
import InStockL2Heading from '@/components/Stocks/Pages/InStockDetailPageV2/InL2Heading'

export const ShareHoldingTable = ({
  table,
  sliceLength = table.tableContent.length,
}: {
  table: {
    tableHeading?: Array<string | JSX.Element>
    tableContent: Array<{
      columns?: Array<JSX.Element | string | number>
      url?: string
      backgroundColor?: string
      name?: string
    }>
    innerBorder?: boolean
    withoutOuterBorder?: boolean
  }
  sliceLength?: number
}) => {
  const [toggle, setToggle] = useState(false)
  const clickToggle = () => setToggle(prevState => !prevState)
  return (
    <>
      <div className=" my-0  overflow-x-auto">
        <table
          className={classNames('my-0 w-full', {
            border: !table.withoutOuterBorder,
          })}
        >
          <thead>
            <tr className="bg-brand-grey-background">
              {table.tableHeading?.map((heading, index) => {
                return (
                  <th
                    className={classNames(
                      'text-13px text-brand-grey-base break-words p-2 text-left font-normal',
                      { 'border-r': table.innerBorder },
                      { 'text-right md:min-w-[130px]': index !== 0 },
                      {
                        'bg-brand-grey-background sticky left-0 min-w-[150px] md:min-w-[250px]':
                          index == 0,
                      },
                    )}
                    key={index}
                  >
                    {heading}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {table.tableContent
              .slice(0, toggle ? table.tableContent.length : sliceLength)
              .map((row, index) => {
                return (
                  <tr
                    key={index}
                    style={{ backgroundColor: row.backgroundColor }}
                    className={classNames({
                      'group cursor-pointer': row.url,
                    })}
                    // onClick added intentionally to make the row clickable
                    onClick={() => {
                      if (row.url) {
                        window.location.href = row.url
                      }
                    }}
                  >
                    {row.columns?.map((data, i) => {
                      return (
                        <td
                          className={classNames(
                            'break-words border-b px-2 py-2.5 text-sm',
                            { 'border-r': table.innerBorder },
                            {
                              'min-w-max text-right md:min-w-[130px]': i !== 0,
                            },
                            {
                              'sticky left-0 min-w-[150px] bg-white md:min-w-[250px]':
                                i == 0,
                            },
                          )}
                          key={i}
                          style={{ backgroundColor: row.backgroundColor }}
                        >
                          {data}
                        </td>
                      )
                    })}
                  </tr>
                )
              })}
          </tbody>
        </table>
      </div>

      {sliceLength < table.tableContent.length && (
        <div
          className="text-brand-blue-link my-2 flex cursor-pointer items-center justify-center text-center font-normal"
          onClick={clickToggle}
        >
          {toggle ? (
            <span className="flex items-center">
              See less{' '}
              <ArrowDown width={20} height={20} className="rotate-180" />
            </span>
          ) : (
            <span className="flex items-center">
              {' '}
              See all <ArrowDown width={20} height={20} />
            </span>
          )}
        </div>
      )}
    </>
  )
}
export default function CataloguePeerComparison({
  data,
  ind_key,
  heading = 'Peer Comparison',
  short_name = '',
}: {
  data: StockPeer[]
  ind_key: string
  heading?: string
  short_name?: string
}) {
  const getTableHeading = mode => {
    if (mode === 'overview') {
      return [
        <div className="w-full" key={1}>
          Company
        </div>,
        'Analyst View',
        <div className="flex flex-col" key={2}>
          Market Cap
        </div>,
        '5 Years Return %',
        'PE Ratio',
        'Profit Margin %',
      ]
    } else if (mode === 'returns') {
      return [
        <div className="w-full" key={1}>
          Company
        </div>,
        ...(data[0].historic_returns?.map(
          item => `${item.label ?? '-'} Returns`,
        ) ?? []),
      ]
    } else
      return [
        <div className="w-full" key={1}>
          Company
        </div>,
        'PE Ratio',
        'PB Ratio',
        'PEG Ratio',
        'EPS',
        'ROE',
        'Dividend Yield',
      ]
  }

  const getTableContent = mode => {
    if (mode === 'overview')
      return data.map(item => {
        const { historic_returns } = item
        const fiveYearReturn = historic_returns?.find(
          item => item.label === '5Y',
        )?.percentage
        return {
          columns: [
            <div
              className="group-hover:text-brand-blue-azureRadiance flex w-full items-center  gap-2"
              key={item.name}
            >
              <div className="flex-shrink-0">
                {item.logo && (
                  <StencilCDNImage
                    src={item.logo}
                    alt={`${item.name} image`}
                    width={30}
                    height={30}
                    className="rounded-full"
                  />
                )}
              </div>
              {item.relativepath ? (
                <Link href={item.relativepath} title={item.name}>
                  {item.name}
                </Link>
              ) : (
                <span>{item.name}</span>
              )}
            </div>,
            item.sentiment ? (
              <span
                className={classNames({
                  'text-brand-red-cinnabar': item.sentiment === 'SELL',
                  'text-brand-green-dark': item.sentiment === 'BUY',
                  'text-brand-yellow-chileanFire': item.sentiment === 'HOLD',
                })}
                key={item.name}
              >
                {item.sentiment}
              </span>
            ) : (
              'NA'
            ),

            item.market_cap_value ? `${item.market_cap_value}` : 'NA',

            fiveYearReturn ? `${fiveYearReturn}%` : 'NA',
            item.pe_ratio === 0 ? 'NA' : item.pe_ratio ?? 'NA',
            item.profit_margin ? `${item.profit_margin}` : 'NA',
          ],
          url: item.relativepath ?? '',
          backgroundColor:
            item.ind_key === ind_key ? 'rgb(232,244,253)' : 'white',
          name: item.name ?? '',
        }
      })
    else if (mode === 'returns') {
      return data.map(item => {
        return {
          columns: [
            <div
              className="group-hover:text-brand-blue-azureRadiance bg-brandt flex w-full  items-center gap-2"
              key={item.name}
            >
              <div className="flex-shrink-0">
                {item.logo && (
                  <StencilCDNImage
                    src={item.logo}
                    alt={`${item.name}`}
                    width={30}
                    height={30}
                    className="rounded-full"
                  />
                )}
              </div>
              {item.relativepath ? (
                <Link href={item.relativepath} title={item.name}>
                  {item.name}
                </Link>
              ) : (
                <span>{item.name}</span>
              )}
            </div>,
            ...(item.historic_returns?.map(item => {
              if (item.percentage) {
                return (
                  <span
                    key={item.label}
                    className={classNames({
                      'text-brand-red-cinnabar': item.percentage < 0,
                      'text-brand-green-dark': item.percentage >= 0,
                    })}
                  >
                    {item.percentage}%
                  </span>
                )
              } else {
                return 'NA'
              }
            }) ?? []),
          ],
          url: item.relativepath ?? '',
          backgroundColor:
            item.ind_key === ind_key ? 'rgb(232,244,253)' : 'white',
          name: item.name,
        }
      })
    } else {
      return data.map(item => {
        return {
          columns: [
            <div
              className="group-hover:text-brand-blue-azureRadiance flex w-full items-center gap-2"
              key={item.name}
            >
              <div className="flex-shrink-0">
                {item.logo && (
                  <StencilCDNImage
                    src={item.logo}
                    alt={`${item.name}`}
                    width={30}
                    height={30}
                    className="rounded-full"
                  />
                )}
              </div>
              {item.relativepath ? (
                <Link href={item.relativepath} title={item.name}>
                  {item.name}
                </Link>
              ) : (
                <span>{item.name}</span>
              )}
            </div>,
            item.pe_ratio === 0 ? 'NA' : item.pe_ratio ?? 'NA',
            item.pb_ratio === 0 ? 'NA' : item.pb_ratio ?? 'NA',
            item.peg_ratio === 0 ? 'NA' : item.peg_ratio ?? 'NA',
            item.eps === 0 ? 'NA' : item.eps ?? 'NA',
            item.roe === 0 ? 'NA' : item.roe ?? 'NA',
            item.dividend_yield ? `${item.dividend_yield}` : 'NA',
          ],
          url: item.relativepath ?? '',
          backgroundColor:
            item.ind_key === ind_key ? 'rgb(232,244,253)' : 'white',
          name: item.name,
        }
      })
    }
  }

  const [selected, setSelected] = useState('overview')
  const tableData = {
    tableHeading: getTableHeading(selected),
    tableContent: getTableContent(selected),
    innerBorder: false,
  }

  const tabs = [
    {
      name: 'Overview',
      id: 'overview',
    },
    {
      name: 'Returns %',
      id: 'returns',
    },
    {
      name: 'Ratios',
      id: 'ratios',
    },
  ]

  return (
    <div>
      {heading && (
        <InStockL2Heading
          heading={heading}
          subheading={`Compare market cap, revenue, PE, and other key metrics of ${short_name} with its industry peers.`}
        />
      )}
      <div className="no-scrollbar my-5 flex w-full items-center gap-2 overflow-scroll md:gap-3">
        {tabs.map(item => {
          return (
            <button
              className={classNames(
                'border-1 border-brand-blue-azureRadiance min-w-fit rounded-full px-4 py-1 text-sm font-normal transition-colors',
                {
                  ' bg-brand-blue-azureRadiance text-brand-white':
                    selected === item.id,
                  'bg-brand-white text-brand-blue-azureRadiance':
                    selected !== item.id,
                },
              )}
              onClick={() => setSelected(item.id)}
              key={item.id}
            >
              {item.name}
            </button>
          )
        })}
      </div>
      <ShareHoldingTable
        table={{ ...tableData, withoutOuterBorder: true }}
        sliceLength={5}
      />
    </div>
  )
}
